import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

/* ──────────────────────────────────────────────────────────────────
   userSlice — auth state for myERP
   ──────────────────────────────────────────────────────────────────
   Two-step login flow:

     Step 1: fetchLoginOptions(emp_id, password)
       → POST /api/entity/getLoginOptions
       → validates credentials, returns user's authorized
         companies / plants / divisions / fin-years (no token)

     Step 2: loginUser(emp_id, password,
                       entity_code, div_group, div_code, fin_yr)
       → POST /api/users/login (extended — accepts context now)
       → re-validates context, issues JWT with context embedded,
         populates the store

   State fields (legacy + new):
     • Legacy (raw strings from user_mast — kept for back-compat):
       entity, division, acc_year
     • New canonical (the picked context):
       entity_code, div_group, div_code, fin_yr
     • Identity:
       emp_id, name, token, module_code

   Persisted to localStorage so a browser refresh keeps the user
   logged in until token exp.
   ────────────────────────────────────────────────────────────────── */

const STORAGE_KEY = "myerp.auth.v1"

/* ── persistence helpers ─────────────────────────────────────── */

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || typeof data.token !== "string") return null
    return data
  } catch (_e) {
    return null
  }
}

const savePersisted = (state) => {
  try {
    const slim = {
      emp_id:      state.emp_id,
      name:        state.name,
      token:       state.token,
      // Legacy strings
      entity:      state.entity,
      division:    state.division,
      acc_year:    state.acc_year,
      module_code: state.module_code,
      // Canonical context
      entity_code: state.entity_code,
      div_group:   state.div_group,
      div_code:    state.div_code,
      fin_yr:      state.fin_yr,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slim))
  } catch (_e) { /* quota / privacy mode — non-fatal */ }
}

const clearPersisted = () => {
  try { localStorage.removeItem(STORAGE_KEY) } catch (_e) { /* noop */ }
}

/* ── initial state (rehydrate on boot) ───────────────────────── */

const persisted = loadPersisted()

const initialState = {
  // Identity
  emp_id:        persisted?.emp_id      ?? null,
  name:          persisted?.name        ?? null,
  token:         persisted?.token       ?? null,
  module_code:   persisted?.module_code ?? null,

  // Legacy raw strings from user_mast (whitespace-separated)
  entity:        persisted?.entity      ?? null,
  division:      persisted?.division    ?? null,
  acc_year:      persisted?.acc_year    ?? null,

  // Canonical context (the user's picked single values)
  entity_code:   persisted?.entity_code ?? null,
  div_group:     persisted?.div_group   ?? null,
  div_code:      persisted?.div_code    ?? null,
  fin_yr:        persisted?.fin_yr      ?? null,

  // Login flow state
  status:        "idle",        // idle | options-loading | options-ready
                                //   | loading | succeeded | failed
  error:         null,
  loginOptions:  null,
}

/* ── thunks ──────────────────────────────────────────────────── */

// Step 1
export const fetchLoginOptions = createAsyncThunk(
  "user/fetchLoginOptions",
  async ({ emp_id, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post("/api/entity/getLoginOptions", {
        emp_id, password,
      })
      return data    // { user, options }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.response?.data           ||
        "Login failed. Please try again."
      )
    }
  }
)

// Step 2
export const loginUser = createAsyncThunk(
  "user/login",
  async (
    { emp_id, password, entity_code, div_group, div_code, fin_yr },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axios.post("/api/users/login", {
        emp_id, password,
        entity_code, div_group, div_code, fin_yr,
      })
      return data
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.response?.data           ||
        "Login failed. Please try again."
      )
    }
  }
)

/* ── slice ───────────────────────────────────────────────────── */

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state) => {
      state.emp_id        = null
      state.name          = null
      state.token         = null
      state.module_code   = null
      state.entity        = null
      state.division      = null
      state.acc_year      = null
      state.entity_code   = null
      state.div_group     = null
      state.div_code      = null
      state.fin_yr        = null
      state.status        = "idle"
      state.error         = null
      state.loginOptions  = null
      clearPersisted()
    },
    clearLoginOptions: (state) => {
      state.loginOptions = null
      state.status       = "idle"
      state.error        = null
    },
    /** Switch context without re-typing password. */
    switchContext: (state, { payload }) => {
      const { entity_code, div_group, div_code, fin_yr } = payload || {}
      if (entity_code !== undefined) state.entity_code = entity_code
      if (div_group   !== undefined) state.div_group   = div_group
      if (div_code    !== undefined) state.div_code    = div_code
      if (fin_yr      !== undefined) state.fin_yr      = fin_yr
      savePersisted(state)
    },
  },
  extraReducers: (builder) => {
    builder
      // Step 1
      .addCase(fetchLoginOptions.pending, (state) => {
        state.status        = "options-loading"
        state.error         = null
        state.loginOptions  = null
      })
      .addCase(fetchLoginOptions.fulfilled, (state, { payload }) => {
        state.status        = "options-ready"
        state.error         = null
        state.loginOptions  = payload.options
        // Stash the user identity for "Hi <name>" on the picker step
        state.emp_id        = payload.user?.emp_id      ?? state.emp_id
        state.name          = payload.user?.name        ?? state.name
        state.module_code   = payload.user?.module_code ?? state.module_code
      })
      .addCase(fetchLoginOptions.rejected, (state, { payload }) => {
        state.status        = "failed"
        state.error         = payload
        state.loginOptions  = null
      })

      // Step 2
      .addCase(loginUser.pending, (state) => {
        state.status = "loading"
        state.error  = null
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.status        = "succeeded"
        state.error         = null
        // Identity
        state.emp_id        = payload.emp_id
        state.name          = payload.Name
        state.token         = payload.token
        state.module_code   = payload.module_code
        // Legacy strings
        state.entity        = payload.entity
        state.division      = payload.division
        state.acc_year      = payload.acc_year
        // Canonical context
        state.entity_code   = payload.entity_code
        state.div_group     = payload.div_group
        state.div_code      = payload.div_code
        state.fin_yr        = payload.fin_yr
        state.loginOptions  = null
        savePersisted(state)
      })
      .addCase(loginUser.rejected, (state, { payload }) => {
        state.status        = "failed"
        state.error         = payload
        state.token         = null
        state.entity_code   = null
        state.div_group     = null
        state.div_code      = null
        state.fin_yr        = null
        clearPersisted()
      })
  },
})

export const { logout, clearLoginOptions, switchContext } = userSlice.actions

/* ── selectors ───────────────────────────────────────────────── */

export const selectUser         = (s) => s.user
export const selectToken        = (s) => s.user.token
export const selectEmpId        = (s) => s.user.emp_id
export const selectUserName     = (s) => s.user.name
export const selectModuleCode   = (s) => s.user.module_code

// Canonical context (preferred for new code)
export const selectEntityCode   = (s) => s.user.entity_code
export const selectDivGroup     = (s) => s.user.div_group
export const selectDivCode      = (s) => s.user.div_code
export const selectFinYear      = (s) => s.user.fin_yr

// Legacy raw strings from user_mast (whitespace-separated)
export const selectEntityString   = (s) => s.user.entity
export const selectDivisionString = (s) => s.user.division
export const selectAccYearString  = (s) => s.user.acc_year

// Backward-compat aliases — keep old screens working
export const selectEntity      = selectEntityString    // raw string, NOT entity_code
export const selectDivision    = selectDivisionString  // raw string, NOT div_code
export const selectAccYear     = selectFinYear         // most callers really wanted picked FY

export const selectLoginStatus  = (s) => s.user.status
export const selectLoginError   = (s) => s.user.error
export const selectLoginOptions = (s) => s.user.loginOptions

/**
 * Bundle selector — pass the whole context to query/SP callers.
 *
 *   const auth = useSelector(selectAuthContext)
 *   api.get(url, { params: auth })
 */
export const selectAuthContext = (s) => ({
  emp_id:      s.user.emp_id,
  entity_code: s.user.entity_code,
  div_group:   s.user.div_group,
  div_code:    s.user.div_code,
  fin_yr:      s.user.fin_yr,
})

export const selectIsAuthenticated = (s) => Boolean(s.user.token)

// Old Header.js compat
export const logoutUser = logout

export default userSlice.reducer
